import clsx from 'clsx';
import type * as React from 'react';

import { type AvatarSize, Tooltip } from '@gitbook/spine-react';

import { UserAvatar, UserAvatarSkeleton, type UserId, type UserObject } from './UserAvatar';

// Retrieve any of the possible ways an id lives on the `user` prop
export function getIdOnUser(user: UserData): string {
    if (typeof user === 'string') {
        return user;
    }
    if ('id' in user) {
        return user.id;
    }
    if ('uid' in user) {
        return user.uid;
    }
    return user.objectID;
}

// Type guard to check if a UserData object includes a pageId
export function hasPageId(participant: UserData): participant is { id: UserId; pageId?: string } {
    return typeof participant === 'object' && 'id' in participant && 'pageId' in participant;
}

export type UserData = UserId | UserObject | { id: UserId; pageId?: string };
export type UsersData = UserData[];

export type AvatarStackProps = {
    /**
     * The users to display, can be an array of userId or user objects.
     */
    users: UsersData;

    /**
     * The size of the avatars.
     * @default 'sm'
     */
    size?: AvatarSize;

    max?: number;

    /** Indicate that users are active */
    realtime?: boolean;

    /**
     * Show a tooltip
     */
    tooltip?: boolean;

    /** Callback when an individual avatar gets clicked */
    onAvatarPress?: (participant: UserData) => void;
};

function isPlainId(user: UserData): user is { id: string } {
    return Object.keys(user).length === 1 && typeof (user as { id: UserId }).id === 'string';
}

const avatarOffsetMap: Record<AvatarSize, string> = {
    xs: '-mr-1.5',
    sm: '-mr-2',
    md: '-mr-3',
    lg: '-mr-5',
};

const sizesMap: Record<AvatarSize, string> = {
    xs: 'size-3.5',
    sm: 'size-6',
    md: 'size-8',
    lg: 'size-12',
};

const DEFAULT_SIZE = 'sm';

/**
 * Horizontal stack of user avatars.
 */
export function UserAvatarStack(props: AvatarStackProps): React.ReactElement | null {
    const { users, size = DEFAULT_SIZE, realtime = false, max = 0, tooltip, onAvatarPress } = props;

    if (!users) {
        return null;
    }

    const shownUsers = max > 0 ? users.slice(0, max) : users;
    const otherUsers = max > 0 ? users.slice(max) : [];

    return (
        <div className="flex items-center">
            {shownUsers.map((user, index) => {
                // Dont offset the avatar if its 1. the last avatar and only if 2. the +N badge is showing
                const dontOffsetAvatar = index === shownUsers.length - 1 && otherUsers.length === 0;

                return (
                    <div
                        key={getIdOnUser(user)}
                        className={clsx(
                            onAvatarPress && 'cursor-pointer',
                            !dontOffsetAvatar && avatarOffsetMap[size]
                        )}
                        onClick={() => onAvatarPress?.(user)}
                    >
                        <UserAvatar
                            user={isPlainId(user) ? user.id : user}
                            size={size}
                            realtime={realtime}
                            tooltipPrefix={onAvatarPress && 'Find'}
                            inStack
                            tooltip={tooltip}
                        />
                    </div>
                );
            })}
            {otherUsers.length > 0 && (
                <Tooltip label={`${otherUsers.length} other users`}>
                    <div
                        className={clsx(
                            'z-[3] box-content flex items-center justify-center rounded-full border-2 border-[--sp-color-border-avatar] bg-[--sp-color-bg-standout-on-base]',
                            sizesMap[size]
                        )}
                    >
                        <span className="mt-0.5 font-bold text-[11px] text-muted text-ui-small">
                            +{otherUsers.length}
                        </span>
                    </div>
                </Tooltip>
            )}
        </div>
    );
}

export function UserAvatarStackSkeleton(props: Pick<AvatarStackProps, 'size'>) {
    const { size = DEFAULT_SIZE } = props;
    return <UserAvatarSkeleton size={size} inStack />;
}
